/**
 * CORE-BN — Front-of-House Attention Capacity · vocabulary
 *
 * Constitutional authority:
 *   `docs/constitution/07 - Feature Library/02 - Core/
 *    C-16 Front-of-House Attention Capacity/
 *    CORE-BN - Front-of-house attention capacity.md`
 *
 * Doctrine this file encodes
 * --------------------------
 *   - "Front-of-house attention capacity is the shop's representation of
 *     the finite attention available to its non-technician operational
 *     staff, and of the degree to which current and planned workload
 *     consumes it."
 *   - Responsibility 2: recognise the DISTINCT DEMANDS that consume
 *     attention "rather than counting tickets". Hence
 *     `FohAttentionClaimKind` is a named vocabulary, not a ticket count.
 *   - Responsibility 6: "Refuse to overstate certainty." Every result
 *     carries `isEstimate: true` and an explicit confidence + basis.
 *   - Responsibility 7: "Never become a performance measure." NOTHING in
 *     this file has a person-shaped field. There is no advisor id, no
 *     name, no per-person load. The unit of every observation is a COUNT.
 *   - Responsibility 8: "Degrade to today's behaviour" — hence
 *     `'unknown'` is a first-class saturation state with reasons.
 *   - SP-05: the output is a DERIVATION (the platform computes it from
 *     authoritative operational evidence), and says so.
 *   - SP-06 / CORE-BN-PRIV-001: "aggregate is not automatically
 *     anonymous". The mitigation here is structural — the model is never
 *     handed person identifiers in the first place, so no output can be
 *     decomposed back to one.
 *
 * Purity: type-only module. No I/O, no clock, no React.
 */

// =============================================================================
// Saturation vocabulary
// =============================================================================

/**
 * The four constitutional saturation bands, plus the mandatory
 * `'unknown'` degradation state.
 *
 * The constitution's Operational Flow names exactly four bands:
 * comfortable / loaded / saturated / over capacity, and requires a fifth
 * outcome — "NO BASIS TO ESTIMATE -> state so plainly, plan as today".
 * `'unknown'` is that outcome. It is NOT a band; it is the absence of a
 * band, and consumers must treat it as "the shop plans as it does now".
 */
export type FohSaturationState =
  | 'comfortable'
  | 'loaded'
  | 'saturated'
  | 'over_capacity'
  | 'unknown';

export const FOH_SATURATION_STATES = Object.freeze([
  'comfortable',
  'loaded',
  'saturated',
  'over_capacity',
  'unknown',
] as const) satisfies ReadonlyArray<FohSaturationState>;

/** Ordered, worsening. `'unknown'` is deliberately absent — it has no rank. */
export const FOH_SATURATION_BAND_ORDER = Object.freeze([
  'comfortable',
  'loaded',
  'saturated',
  'over_capacity',
] as const);

export type FohSaturationBand = (typeof FOH_SATURATION_BAND_ORDER)[number];

/** Human-facing labels. Shop-scoped language only — never person-scoped. */
export const FOH_SATURATION_LABELS: Readonly<Record<FohSaturationState, string>> =
  Object.freeze({
    comfortable: 'Comfortable',
    loaded: 'Loaded',
    saturated: 'Saturated',
    over_capacity: 'Over capacity',
    unknown: 'Not enough basis to estimate',
  });

// =============================================================================
// Claim vocabulary
// =============================================================================

/**
 * The named demands that consume front-of-house attention.
 *
 * Constitutional Responsibility 2 enumerates them as "arrival and
 * intake, customer communication, decision and approval work, custody
 * actions, and technician support". Custody actions are split by custody
 * state because the three FOH custody states are not equivalent claims:
 * a ticket waiting to be accepted is a queue the shop has not reached,
 * a ticket on the desk is active work, and a ticket kicked back to a
 * technician is mostly the technician's claim, not the front desk's.
 *
 *   - `arrival_intake`              — a vehicle arriving / being dropped
 *                                     off in the window. Discrete and
 *                                     bounded.
 *   - `waiter_present`              — a customer waiting in the lobby.
 *                                     NOT discrete: presence claims
 *                                     attention continuously for as long
 *                                     as it lasts (constitution: "Present
 *                                     customers claim attention
 *                                     continuously").
 *   - `custody_pending_accept`      — a ticket sitting in
 *                                     `pending_advisor_accept`.
 *   - `custody_with_advisor`        — a ticket actively held in
 *                                     `with_advisor`.
 *   - `custody_pending_tech_accept` — a ticket kicked back, sitting in
 *                                     `pending_tech_accept`. Low
 *                                     front-desk cost: the technician is
 *                                     the actor.
 *   - `customer_communication`      — calls and outbound messages.
 *   - `decision_approval`           — advisor decision / approval work.
 *   - `technician_support`          — a technician at the desk needing an
 *                                     answer. Constitutionally required
 *                                     in the vocabulary; the shop does
 *                                     not currently observe it directly,
 *                                     so it is normally reported
 *                                     UNOBSERVED rather than assumed to
 *                                     be zero.
 */
export type FohAttentionClaimKind =
  | 'arrival_intake'
  | 'waiter_present'
  | 'custody_pending_accept'
  | 'custody_with_advisor'
  | 'custody_pending_tech_accept'
  | 'customer_communication'
  | 'decision_approval'
  | 'technician_support';

export const FOH_ATTENTION_CLAIM_KINDS = Object.freeze([
  'arrival_intake',
  'waiter_present',
  'custody_pending_accept',
  'custody_with_advisor',
  'custody_pending_tech_accept',
  'customer_communication',
  'decision_approval',
  'technician_support',
] as const) satisfies ReadonlyArray<FohAttentionClaimKind>;

export const FOH_ATTENTION_CLAIM_LABELS: Readonly<
  Record<FohAttentionClaimKind, string>
> = Object.freeze({
  arrival_intake: 'Arrivals and drop-offs',
  waiter_present: 'Customers waiting in the lobby',
  custody_pending_accept: 'Tickets awaiting front-desk acceptance',
  custody_with_advisor: 'Tickets on the front desk',
  custody_pending_tech_accept: 'Tickets returned to a technician',
  customer_communication: 'Customer calls and messages',
  decision_approval: 'Decisions and approvals',
  technician_support: 'Technician questions at the desk',
});

/**
 * Custody claim kinds only. Dwell escalation applies to exactly these,
 * because dwell is a property of custody, not of an arrival or a call.
 */
export const FOH_CUSTODY_CLAIM_KINDS = Object.freeze([
  'custody_pending_accept',
  'custody_with_advisor',
  'custody_pending_tech_accept',
] as const) satisfies ReadonlyArray<FohAttentionClaimKind>;

export type FohCustodyClaimKind = (typeof FOH_CUSTODY_CLAIM_KINDS)[number];

export function isCustodyClaimKind(
  kind: FohAttentionClaimKind,
): kind is FohCustodyClaimKind {
  return (FOH_CUSTODY_CLAIM_KINDS as ReadonlyArray<string>).includes(kind);
}

/**
 * How a claim's count was arrived at. This is provenance, and it is
 * mandatory: the constitution forbids showing a number without the
 * basis that produced it.
 *
 *   - `'live'`          — OBSERVED from current operational truth as of
 *                         the window start. The shop holds direct
 *                         evidence: a custody row, a vehicle physically
 *                         at the shop.
 *   - `'inferred'`      — derived from the SCHEDULE rather than observed.
 *                         A vehicle expected to arrive has not arrived;
 *                         the shop has no arrival event, only an
 *                         appointment. Contributes to the estimate, but
 *                         is never presented as an observation and caps
 *                         confidence below `'high'`.
 *   - `'trailing_rate'` — counted from the immediately PRECEDING window
 *                         of equal length and used as the estimate for
 *                         this one. Genuinely an extrapolation, and
 *                         labelled as one.
 *   - `'unobserved'`    — the shop does not capture this claim. The
 *                         count is not zero; it is unknown, so the claim
 *                         contributes nothing and lowers confidence.
 *   - `'unavailable'`   — the source exists but could not be read this
 *                         time (query failure, bad payload). Contributes
 *                         nothing, lowers confidence, is recorded.
 *
 * The `'live'` / `'inferred'` split exists because the shop has **no
 * arrival event**. Presenting a scheduled appointment as an observed
 * arrival would be exactly the kind of certainty Responsibility 6
 * forbids: "an estimate presented as a measurement is a lie with a
 * number attached."
 */
export type FohClaimBasis =
  | 'live'
  | 'inferred'
  | 'trailing_rate'
  | 'unobserved'
  | 'unavailable';

/**
 * Bases that mean "this claim genuinely contributed a counted quantity".
 * The others mean "we do not know", which is not the same as zero.
 */
export const FOH_CONTRIBUTING_BASES = Object.freeze([
  'live',
  'inferred',
  'trailing_rate',
] as const) satisfies ReadonlyArray<FohClaimBasis>;

export function isContributingBasis(basis: FohClaimBasis): boolean {
  return (FOH_CONTRIBUTING_BASES as ReadonlyArray<string>).includes(basis);
}

/**
 * Bases that contribute a quantity the shop did NOT directly observe.
 * These cap confidence: the estimate rests partly on what the schedule
 * predicted or what the last window did, not on what is true now.
 */
export const FOH_UNOBSERVED_CONTRIBUTING_BASES = Object.freeze([
  'inferred',
  'trailing_rate',
] as const) satisfies ReadonlyArray<FohClaimBasis>;

export function isDirectlyObservedBasis(basis: FohClaimBasis): boolean {
  return basis === 'live';
}

/**
 * How a counted claim splits between what was physically observed and
 * what was inferred from the schedule.
 *
 * Carried so a surface can say "two customers in the lobby, one more
 * expected" rather than flattening both into a single unqualified
 * number. Sums to the claim's `count`.
 */
export interface FohPresenceSplit {
  /** Backed by physical evidence the shop holds right now. */
  observed: number;
  /** Backed only by an appointment time. */
  inferred: number;
}

/** Dwell escalation buckets, mirroring `custodyAging`'s levels. */
export type FohDwellBucket = 'normal' | 'warning' | 'urgent';

export const FOH_DWELL_BUCKETS = Object.freeze([
  'normal',
  'warning',
  'urgent',
] as const) satisfies ReadonlyArray<FohDwellBucket>;

export type FohDwellDistribution = Readonly<Record<FohDwellBucket, number>>;

// =============================================================================
// Observations (the model's input)
// =============================================================================

/**
 * One observed claim kind, as handed to the model.
 *
 * `dwell` is optional and only meaningful for custody claims. When
 * present, the counts across buckets should sum to `count`; the model
 * defends against callers that violate this rather than trusting it.
 *
 * There is deliberately no `userId`, `advisorId`, `holderId` or name on
 * this shape. A claim is a quantity of demand on the shop, not a
 * person's workload.
 */
export interface FohClaimObservation {
  kind: FohAttentionClaimKind;
  /** Non-negative count of occurrences. */
  count: number;
  basis: FohClaimBasis;
  /** Custody-only: distribution of `count` across dwell buckets. */
  dwell?: FohDwellDistribution;
  /**
   * Presence-based claims only: how much of `count` rests on physical
   * evidence and how much on the schedule alone.
   */
  presence?: FohPresenceSplit;
}

/**
 * How available front-of-house attention was established.
 *
 *   - `'clocked_in'`    — people actually clocked in right now, filtered
 *                         to front-of-house-capable roles. Strongest,
 *                         and **authoritative including when it is
 *                         zero** — see `FohTimeClockStatus`.
 *   - `'roster'`        — active front-of-house-capable memberships. The
 *                         roster is not the same as who is on the floor,
 *                         so this is weaker.
 *   - `'shop_override'` — the shop stated its front-of-house headcount in
 *                         policy. Authoritative because a human said it.
 *   - `'none'`          — no basis at all. Forces `'unknown'`.
 */
export type FohStaffingBasis =
  | 'clocked_in'
  | 'roster'
  | 'shop_override'
  | 'none';

/**
 * What the shop's time clock could tell us about this window.
 *
 * This distinction is load-bearing, and collapsing it is how a capacity
 * model fabricates staff.
 *
 *   - `'available'` — the shop uses the time clock, and the clock (and
 *                     the role roster needed to filter it) were both read
 *                     successfully. **Its answer is authoritative even
 *                     when that answer is zero.** A successfully-read
 *                     clock reporting nobody on a front-of-house role is
 *                     positive evidence that nobody is on the desk; it is
 *                     not an absence of evidence, and it must not be
 *                     replaced by the roster.
 *   - `'disabled'`  — the shop does not use the time clock. The roster is
 *                     then the best statement available of who works
 *                     here, and falling back to it is legitimate.
 *   - `'unavailable'` — the shop may or may not use the clock, but we
 *                     could not read it (or could not read the roster
 *                     needed to filter it). We do not know what it would
 *                     have said, so the roster is used as a weaker basis
 *                     and the failure is recorded.
 */
export type FohTimeClockStatus = 'available' | 'disabled' | 'unavailable';

/**
 * Front-of-house staffing for the window, as a COUNT.
 *
 * The count is the only staffing fact the model receives. It cannot name
 * anyone because it is never told anyone's name.
 */
export interface FohStaffingObservation {
  /**
   * Non-negative headcount. `0` is a valid observation. Whether it means
   * "nobody is on" or "we could not tell" is carried by `basis` +
   * `timeClockStatus`, not by the number.
   */
  headcount: number;
  basis: FohStaffingBasis;
  /** What the time clock could tell us. Drives which basis was chosen. */
  timeClockStatus: FohTimeClockStatus;
}

/**
 * The complete input to the model.
 *
 * `shopId` is carried so the model can refuse to mix shops (SP-04).
 * `windowStartIso` / `windowEndIso` are injected by the caller — the
 * model never reads the clock, which is what makes it deterministic
 * under test.
 */
export interface FohAttentionObservations {
  shopId: string;
  windowStartIso: string;
  windowEndIso: string;
  staffing: FohStaffingObservation;
  claims: ReadonlyArray<FohClaimObservation>;
  /** Ids of data sources that failed to read, for the basis report. */
  unavailableSources?: ReadonlyArray<string>;
}

// =============================================================================
// Results (the model's output)
// =============================================================================

/** Whether a weight came from shop policy or from the conservative default. */
export type FohWeightSource = 'default' | 'shop';

/**
 * One contributing claim, fully explained.
 *
 * The constitution: "A number without its contributing claims must not
 * be shown", and "Where the estimate rests on a default weight rather
 * than shop-configured policy, that must be visible." Both are satisfied
 * by this shape being mandatory on every banded result.
 */
export interface FohAttentionClaimContribution {
  kind: FohAttentionClaimKind;
  label: string;
  count: number;
  basis: FohClaimBasis;
  /** Attention-minutes charged per occurrence, before dwell escalation. */
  unitWeightMinutes: number;
  weightSource: FohWeightSource;
  /** Custody-only: the dwell distribution actually used. */
  dwell?: FohDwellDistribution;
  /** Presence-based claims: how much of `count` was observed vs inferred. */
  presence?: FohPresenceSplit;
  /**
   * Custody-only: effective multiplier applied because of dwell, i.e.
   * `attentionMinutes / (count * unitWeightMinutes)`. `1` when no
   * escalation applied.
   */
  dwellEscalation: number;
  /** Total attention-minutes this claim contributes. */
  attentionMinutes: number;
  /** False when the claim contributed nothing because it could not be read. */
  contributed: boolean;
}

/** Confidence in the estimate. Never `'high'` on a thin or extrapolated basis. */
export type FohEstimateConfidence = 'high' | 'moderate' | 'low' | 'none';

/**
 * Why the model could not produce a band. Present only when the state is
 * `'unknown'`.
 */
/**
 * Why the model could not produce a band.
 *
 * `no_foh_staffing` and `no_foh_staff_on_duty` are deliberately
 * different statements, and the difference matters to the person reading
 * the surface. "We could not establish who is on" invites the shop to
 * fix its data. "Nobody is currently clocked in to a front-of-house
 * role" invites the shop to fix its morning.
 */
export type FohInsufficientBasisReason =
  | 'no_foh_staffing'
  | 'no_foh_staff_on_duty'
  | 'invalid_window'
  | 'no_observed_claims'
  | 'shop_mismatch';

export interface FohAttentionBasis {
  staffingBasis: FohStaffingBasis;
  timeClockStatus: FohTimeClockStatus;
  fohHeadcount: number;
  confidence: FohEstimateConfidence;
  /** Claim kinds the shop does not capture at all. */
  unobservedClaimKinds: ReadonlyArray<FohAttentionClaimKind>;
  /** Claim kinds whose source failed this run. */
  unavailableClaimKinds: ReadonlyArray<FohAttentionClaimKind>;
  /** Ids of data sources that failed, for support. */
  unavailableSources: ReadonlyArray<string>;
  /** Claim kinds estimated from the preceding window rather than this one. */
  extrapolatedClaimKinds: ReadonlyArray<FohAttentionClaimKind>;
  /** Claim kinds derived from the schedule rather than physically observed. */
  inferredClaimKinds: ReadonlyArray<FohAttentionClaimKind>;
  /** Fraction of the claim vocabulary that actually contributed, 0..1. */
  observedClaimCoverage: number;
}

/**
 * The resolved policy, with per-field provenance, as applied to one
 * estimate. Snapshotting it on the result is what makes an estimate
 * reconstructable after the fact.
 */
export interface FohAppliedPolicySnapshot {
  claimWeightMinutes: Readonly<Record<FohAttentionClaimKind, number>>;
  claimWeightSources: Readonly<Record<FohAttentionClaimKind, FohWeightSource>>;
  dwellEscalationMultipliers: FohDwellDistribution;
  dwellEscalationSource: FohWeightSource;
  saturationThresholds: Readonly<{
    loaded: number;
    saturated: number;
    overCapacity: number;
  }>;
  saturationThresholdsSource: FohWeightSource;
  personEffectiveAttentionFraction: number;
  personEffectiveAttentionFractionSource: FohWeightSource;
  waiterPresenceWindowFraction: number;
  waiterPresenceWindowFractionSource: FohWeightSource;
  windowMinutes: number;
  windowMinutesSource: FohWeightSource;
  /** True when NO field was shop-configured — the untuned default path. */
  usingAllDefaults: boolean;
}

/**
 * The capability's output.
 *
 * Every field here describes the SHOP. There is no field that names,
 * ranks, scores or compares a person, and adding one would be a
 * constitutional violation, not a feature.
 */
export interface FohAttentionCapacityResult {
  shopId: string;
  windowStartIso: string;
  windowEndIso: string;
  windowMinutes: number;

  /** Constitutional Responsibility 6 — this is an estimate, and says so. */
  readonly isEstimate: true;
  /** SP-05 evidence class. The platform computed this from operational truth. */
  readonly evidenceClass: 'derivation';

  state: FohSaturationState;
  /** Present only when `state !== 'unknown'`. */
  band: FohSaturationBand | null;

  /** Attention-minutes the shop's front of house actually has. */
  availableAttentionMinutes: number;
  /** Attention-minutes the workload is estimated to consume. */
  estimatedConsumedAttentionMinutes: number;
  /** consumed / available. `null` when there is no basis. */
  utilization: number | null;
  /** available minus consumed, floored at 0. `null` when there is no basis. */
  headroomAttentionMinutes: number | null;

  /** Mandatory explainability. Ordered by attention-minutes, descending. */
  contributingClaims: ReadonlyArray<FohAttentionClaimContribution>;
  /** The exact policy that produced this result. */
  appliedPolicy: FohAppliedPolicySnapshot;
  basis: FohAttentionBasis;
  insufficientBasisReasons: ReadonlyArray<FohInsufficientBasisReason>;
}
